/**
 * Platform state:
 *   - health/visibility of DEX & CEX backends (unchanged)
 *   - activePlatform: which "client list" is currently shown on the toggle-aware
 *     pages (Dashboard, Project, TokenGroup). Default = subdomain if we're on
 *     dex.* / cex.* in prod; falls back to a persisted override on localhost.
 *
 * Switching activePlatform is in-app only — it does NOT navigate to the other
 * subdomain. Sidebar items, Logs, Knowledge, Warmup, Distributor, Settings
 * render identically on both subdomains; only pages tied to "clients" react to
 * this flag.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  HEALTH_CONSECUTIVE_FAILS_FOR_OFFLINE,
  pingBoth,
} from '../lib/platformHealth.js'

const PlatformContext = createContext(null)

const POLL_MS = 10_000
const STORAGE_KEY = 'lotos_platform_visibility'
const ACTIVE_PLATFORM_KEY = 'lotos_active_platform'

function initialVisibility() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        dex: parsed.dex !== false,
        cex: parsed.cex !== false,
      }
    }
  } catch {}
  return { dex: true, cex: true }
}

function detectSubdomainPlatform() {
  if (typeof window === 'undefined') return 'dex'
  const h = window.location.hostname
  if (h.startsWith('cex.')) return 'cex'
  if (h.startsWith('dex.')) return 'dex'
  return null
}

function initialActivePlatform() {
  // In prod (dex.*/cex.*) the subdomain wins by default.
  const sub = detectSubdomainPlatform()
  if (sub) return sub
  // Localhost / custom host — fall back to persisted preference.
  try {
    const v = localStorage.getItem(ACTIVE_PLATFORM_KEY)
    if (v === 'cex' || v === 'dex') return v
  } catch {}
  return 'dex'
}

function applyHysteresis(prev, result, dexStreak, cexStreak) {
  const k = HEALTH_CONSECUTIVE_FAILS_FOR_OFFLINE
  let dex
  let cex
  if (result.dex) {
    dexStreak.current = 0
    dex = true
  } else {
    dexStreak.current += 1
    if (dexStreak.current >= k) {
      dex = false
    } else if (prev.dex === true) {
      dex = true
    } else if (prev.dex === null) {
      dex = null
    } else {
      dex = false
    }
  }
  if (result.cex) {
    cexStreak.current = 0
    cex = true
  } else {
    cexStreak.current += 1
    if (cexStreak.current >= k) {
      cex = false
    } else if (prev.cex === true) {
      cex = true
    } else if (prev.cex === null) {
      cex = null
    } else {
      cex = false
    }
  }
  return { dex, cex }
}

export function PlatformProvider({ children }) {
  const [health, setHealth] = useState({ dex: null, cex: null })
  const [visibility, setVisibility] = useState(initialVisibility)
  const [activePlatform, setActivePlatformState] = useState(initialActivePlatform)
  const timerRef = useRef(null)
  const dexFailStreak = useRef(0)
  const cexFailStreak = useRef(0)

  const pollNow = useCallback(async () => {
    const result = await pingBoth()
    setHealth((prev) => applyHysteresis(prev, result, dexFailStreak, cexFailStreak))
  }, [])

  useEffect(() => {
    /** First probe on next microtask (fast liveness) + 10s interval. */
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => void pollNow())
    } else {
      setTimeout(() => void pollNow(), 0)
    }
    timerRef.current = setInterval(pollNow, POLL_MS)
    const onFocus = () => pollNow()
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      clearInterval(timerRef.current)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [pollNow])

  const setShow = useCallback((side, value) => {
    setVisibility((prev) => {
      const next = { ...prev, [side]: value }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {}
      return next
    })
  }, [])

  const setActivePlatform = useCallback((p) => {
    if (p !== 'dex' && p !== 'cex') return
    setActivePlatformState(p)
    try {
      localStorage.setItem(ACTIVE_PLATFORM_KEY, p)
    } catch {}
  }, [])

  const value = useMemo(
    () => ({
      health,
      visibility,
      activePlatform,
      setActivePlatform,
      showDex: visibility.dex && health.dex !== false,
      showCex: visibility.cex && health.cex !== false,
      /** "Available" in UI: only `false` after a failed probe means offline (not `null` while loading). */
      dexAvailable: health.dex !== false,
      cexAvailable: health.cex !== false,
      setShowDex: (v) => setShow('dex', v),
      setShowCex: (v) => setShow('cex', v),
      refresh: pollNow,
    }),
    [health, visibility, activePlatform, setActivePlatform, setShow, pollNow],
  )

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
}

export function usePlatform() {
  const ctx = useContext(PlatformContext)
  if (!ctx) throw new Error('usePlatform must be used within PlatformProvider')
  return ctx
}
